"use client";
import { useEffect, useState } from "react";
import {
  Form,
  Input,
  Select,
  Button,
  message,
  Row,
  Col,
  Space,
  InputNumber,
} from "antd";
import {
  CheckCircleOutlined,
  PhoneOutlined,
  UserOutlined,
  WhatsAppOutlined,
} from "@ant-design/icons";
import { useRouter } from "next/navigation"; // ✅ Import router

// API configuration
const TELECRM_API_URL = "https://next-api.telecrm.in/enterprise/66ec0997759f100018f5cbfe/autoupdatelead";
const TELECRM_BEARER_TOKEN = "03d8ea9a-3d6a-4067-a35b-26f0c11f52a31755503300325:981a1bd8-8510-4456-98db-c96c6977f61c";
const MAKE_WEBHOOK_URL =
  "https://hook.eu2.make.com/3bjhrava380i3b6g1l85rlseyshojeoc";
const WEB3FORMS_ACCESS_KEY = "1af8dd38-c767-42f7-82c2-f76edac26a9f";

// Country codes for phone number
const countryCodes = [
  { code: "+91", country: "India", flag: "🇮🇳" },
  { code: "+1", country: "USA", flag: "🇺🇸" },
  { code: "+44", country: "UK", flag: "🇬🇧" },
  { code: "+61", country: "Australia", flag: "🇦🇺" },
  { code: "+65", country: "Singapore", flag: "🇸🇬" },
  { code: "+971", country: "UAE", flag: "🇦🇪" },
  { code: "+60", country: "Malaysia", flag: "🇲🇾" },
  { code: "+81", country: "Japan", flag: "🇯🇵" },
  { code: "+86", country: "China", flag: "🇨🇳" },
];

export default function VietnamTourPopup() {
  const router = useRouter(); // ✅ Initialize router
  const [form] = Form.useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [selectedCountryCode, setSelectedCountryCode] = useState("+91");

  // Show popup after 10 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowPopup(true);
    }, 10000);
    return () => clearTimeout(timer);
  }, []);

  // Allow other components (e.g. floating Book Now button) to open the popup
  useEffect(() => {
    const handler = () => setShowPopup(true);
    window.addEventListener("open-booking-popup", handler);
    return () => window.removeEventListener("open-booking-popup", handler);
  }, []);

  useEffect(() => {
    form.setFieldsValue({
      international: "Vietnam",
      package: "International",
      pageUrl: window.location.href,
      submissionTime: new Date().toISOString(),
      countryCode: "+91",
      leadSource: "Vietnam Tour Popup",
      groupTour: "Not Specified",
      traveledWithSaishishirtour: "No",
    });
  }, [form]);

  // Send to Web3Forms
  const sendToWeb3Forms = async (data) => {
    const emailPayload = {
      access_key: WEB3FORMS_ACCESS_KEY,
      name: data["your-name"],
      email: data["your-email"] || "info@saishishirtour.in",
      phone: data.fullPhone,
      subject: "New Vietnam Tour Inquiry - Popup Form",
      message: `
New Vietnam Tour Inquiry Details (Popup Form):

Name: ${data["your-name"]}
Phone: ${data.fullPhone}
Email: ${data["your-email"] || "Not provided"}
Selected Package: ${data["tour-month"] || "Not specified"}

Lead Source: ${data.leadSource}
Page URL: ${data.pageUrl}
Submission Time: ${data.submissionTime}
      `,
      reply_to: data["your-email"] || "info@saishishirtour.in",
      from_name: "Vietnam Tour Popup Form",
    };

    const response = await fetch("https://api.web3forms.com/submit", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(emailPayload),
    });

    return response.ok;
  };

  // Send to Make.com webhook
  const sendToMakeWebhook = async (data) => {
    const webhookPayload = {
      formData: data,
      timestamp: new Date().toISOString(),
      source: "Vietnam Tour Popup Form",
      leadSource: "Vietnam Tour Popup",
    };

    const response = await fetch(MAKE_WEBHOOK_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(webhookPayload),
    });

    return response.ok;
  };

  // Send to TeleCRM
  const sendToTeleCRM = async (values) => {
    const fields = {
      name: values["your-name"],
      phone: `${values["countryCode"]}${values["your-number"]}`,
      email: values["your-email"] || "",
      INTL: values["international"] || "Vietnam",
      month: values["tour-month"] || "",
      package: values["package"] || "International",
      phoneNumber: values["your-number"] || "",
      traveledWithSaishishirtour: "No",
      groupTour: "Not Specified",
      leadSource: "Vietnam Tour Popup",
      score: 0,
    };

    const actions = [
      {
        type: "SYSTEM_NOTE",
        text: `Lead Source: Vietnam Tour Popup
Destination: Vietnam
Selected Package: ${values["tour-month"] || "Not specified"}
Package Type: International
Page URL: ${values["pageUrl"]}
Submission Time: ${new Date().toISOString()}`,
      },
    ];

    const teleCrmPayload = { fields, actions };

    const response = await fetch(TELECRM_API_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${TELECRM_BEARER_TOKEN}`,
        Accept: "application/json",
      },
      body: JSON.stringify(teleCrmPayload),
    });

    return response.ok;
  };

  const onFinish = async (values) => {
    setIsSubmitting(true);
    const commonData = {
      ...values,
      fullPhone: `${values["countryCode"]}${values["your-number"]}`,
      submissionTime: new Date().toISOString(),
      pageUrl: window.location.href,
      leadSource: "Vietnam Tour Popup",
    };

    try {
      const results = await Promise.all([
        sendToTeleCRM(values),
        sendToMakeWebhook(commonData),
        sendToWeb3Forms(commonData),
      ]);

      if (results.some((res) => res === true)) {
        
        message.success("Thank you! Redirecting...");
        router.push("/vietnam-thank-you"); // ✅ Instant redirect
      } else {
        throw new Error("All submissions failed");
      }
    } catch (error) {
      console.error("Form submission error:", error);
      message.error("Failed to submit. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCountryCodeChange = (value) => {
    setSelectedCountryCode(value);
    form.setFieldsValue({ countryCode: value });
  };

  const handleWhatsAppClick = () => {
    const message =
      "Hi, I’m interested in your Vietnam tour package. Please share itinerary, inclusions, dates, and cost details.";
    const whatsappUrl = `https://wa.me/918951062993?text=${encodeURIComponent(
      message
    )}`;
    window.open(whatsappUrl, "_blank");
  };

  if (!showPopup) return null;

  return (
    <>
      <div
        className={`modal fade ${showPopup ? "show" : ""}`}
        style={{
          display: showPopup ? "block" : "none",
          backgroundColor: "rgba(0,0,0,0.7)",
        }}
      >
        <div className="modal-dialog modal-dialog-centered modal-md">
          <div
            className="modal-content"
            style={{ borderRadius: "20px", overflow: "hidden" }}
          >
            <div
              className="modal-header blue-bg-color border-0"
              style={{
                background:
                  "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "white",
              }}
            >
              <div className="w-100 text-center">
                <h4 className="modal-title mb-0">
                  Your Vietnam Travel Guide is Ready!
                </h4>
                <p className="mb-0 small">Get FREE Vietnam Itinerary Now</p>
              </div>
              <button
                type="button"
                className="btn-close btn-close-white"
                onClick={() => setShowPopup(false)}
              ></button>
            </div>

            <div className="modal-body p-4">
              <Form
                form={form}
                layout="vertical"
                onFinish={onFinish}
                size="large"
              >
                <Row gutter={[16, 16]}>
                  <Col span={24}>
                    <Form.Item
                      name="your-name"
                      rules={[
                        {
                          required: true,
                          message: "Please enter your name",
                        },
                      ]}
                    >
                      <Input
                        prefix={<UserOutlined className="text-primary" />}
                        placeholder="Your full name"
                        className="rounded-pill"
                        style={{
                          height: "45px",
                          background: "rgba(240, 240, 240, 0.8)",
                          border: "1px solid #e0e0e0",
                        }}
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={8}>
                    <Form.Item name="countryCode">
                      <Select
                        value={selectedCountryCode}
                        onChange={handleCountryCodeChange}
                        className="rounded-pill"
                        style={{ height: "45px" }}
                        optionLabelProp="label"
                      >
                        {countryCodes.map((item) => (
                          <Select.Option
                            key={item.code}
                            value={item.code}
                            label={`${item.flag} ${item.code}`}
                          >
                            <Space>
                              <span>{item.flag}</span>
                              <span>{item.code}</span>
                            </Space>
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={16}>
                    <Form.Item
                      name="your-number"
                      rules={[
                        {
                          required: true,
                          message: "Please enter your phone number",
                        },
                        {
                          pattern: /^[0-9]{10,12}$/,
                          message: "Please enter a valid phone number",
                        },
                      ]}
                    >
                      <Input
                        prefix={<PhoneOutlined className="text-primary" />}
                        placeholder="Phone number"
                        className="rounded-pill"
                        style={{
                          height: "45px",
                          background: "rgba(240, 240, 240, 0.8)",
                          border: "1px solid #e0e0e0",
                        }}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                {/* Hidden Fields */}
                <Form.Item name="pageUrl" hidden>
                  <Input />
                </Form.Item>
                <Form.Item name="submissionTime" hidden>
                  <Input />
                </Form.Item>
                <Form.Item name="leadSource" hidden>
                  <Input />
                </Form.Item>
                <Form.Item name="international" hidden>
                  <Input />
                </Form.Item>
                <Form.Item name="package" hidden>
                  <Input />
                </Form.Item>
                <Form.Item name="groupTour" hidden>
                  <Input />
                </Form.Item>
                <Form.Item name="traveledWithSaishishirtour" hidden>
                  <Input />
                </Form.Item>
                <Form.Item name="score" hidden>
                  <InputNumber />
                </Form.Item>

                <Form.Item className="mt-3 mb-4">
                  <Button
                    type="primary"
                    htmlType="submit"
                    block
                    size="large"
                    loading={isSubmitting}
                    className="rounded-pill"
                    style={{
                      height: "50px",
                      fontSize: "16px",
                      fontWeight: "600",
                      background:
                        "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      border: "none",
                      boxShadow: "0 4px 15px rgba(102, 126, 234, 0.4)",
                    }}
                  >
                    {isSubmitting ? "Sending..." : "🎯 Get Best Price Now!"}
                  </Button>
                </Form.Item>
              </Form>

              <div className="border-top pt-3">
                <p className="text-center text-muted small mb-3">
                  Wanted Itinerary + Cost Instantly, then Click Below
                </p>
                <div className="row d-flex justify-content-center g-2">
                  <div className="col-12 col-md-4">
                    <button
                      className="btn btn-success w-100 rounded-pill"
                      onClick={handleWhatsAppClick}
                      style={{ height: "45px" }}
                    >
                      <WhatsAppOutlined
                        className="me-2"
                        style={{ fontSize: "18px" }}
                      />
                      <small>WhatsApp</small>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx global>{`
        .modal.fade .modal-dialog {
          transform: scale(0.8) translateY(-50px);
          transition: all 0.3s ease-out;
        }
        .modal.show .modal-dialog {
          transform: scale(1) translateY(0);
        }
      `}</style>
    </>
  );
}
